// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngCarousel, { SwiperStyle } from "@ces/App/Components/XngCarousel/XngCarousel";
import loadFeaturedCategories from "@ces/App/Handlers/XNG/FeaturedCategories";
import { CustomAppState } from "@ces/App/Store/Reducers";
import { XNGAGE_CUSTOM_WIDGET } from "@ces/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Category } from "@insite/client-framework/Services/CategoryService";
import { getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    selectedCategories = "selectedCategories",
    maxNumberOfCategories = "maxNumberOfCategories",
}

interface OwnProps extends WidgetProps {
    fields: {
        selectedCategories: string[];
        maxNumberOfCategories: number;
    };
}

const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    const key = getDataViewKey({ categories: props.fields?.selectedCategories });
    const categoriesState = (state as CustomAppState).xFeaturedCategoriesData.view?.[key];
    return {
        parentCategories: categoriesState?.categories,
        isLoading: categoriesState?.isLoading,
    };
};

const mapDispatchToProps = {
    loadFeaturedCategories,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductCategoriesStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: InjectableCss;
    categoriesWrapper?: InjectableCss;
    categoryWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    image?: LazyImageProps;
    imageWrapper?: InjectableCss;
    swiperStyles?: SwiperStyle;
}

export const productCategoriesStyles: ProductCategoriesStyles = {};

const styles = productCategoriesStyles;

interface CategoryCardProps {
    category: Category;
}

const CategoryCard: FC<CategoryCardProps> = ({ category }) => {
    return (
        <StyledWrapper key={category.id} {...styles.categoryWrapper}>
            <Clickable href={category.path} {...styles.imageWrapper} aria-label="category image clickable">
                <LazyImage
                    width="141px"
                    height="141px"
                    src={category.smallImagePath}
                    altText={category.shortDescription}
                    {...styles.image}
                />
            </Clickable>
            <Link href={category.path} {...styles.link} area-label={category.shortDescription}>
                {category.shortDescription}
            </Link>
        </StyledWrapper>
    );
};

const ProductCategoriesWidget: React.FC<Props> = ({ fields, loadFeaturedCategories, parentCategories, isLoading }) => {
    React.useEffect(() => {
        if (!isLoading && !parentCategories && fields.selectedCategories && fields.selectedCategories.length !== 0) {
            loadFeaturedCategories({
                categories: fields.selectedCategories,
            });
        }
    }, [isLoading, fields.selectedCategories]);

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper} data-test-selector="featuredCategories_Loading">
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (!parentCategories?.length) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            <XngCarousel
                extendedStyles={styles.swiperStyles}
                scrollbar
                spaceBetween={22}
                breakpoints={{
                    360: {
                        slidesPerView: 2,
                    },
                    768: {
                        slidesPerView: 4.5,
                    },
                    1024: {
                        slidesPerView: 6.5,
                    },
                    1200: {
                        slidesPerView: 7.5,
                    },
                }}
            >
                {parentCategories.slice(0, fields.maxNumberOfCategories).map(category => (
                    <CategoryCard key={category.id} category={category} />
                ))}
            </XngCarousel>
        </StyledWrapper>
    );
};

const productCategories: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductCategoriesWidget),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET as any,
        fieldDefinitions: [
            {
                name: fields.selectedCategories,
                displayName: "Select Category",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "General",
            },
            {
                name: fields.maxNumberOfCategories,
                displayName: "Max Number Of Category",
                editorTemplate: "IntegerField",
                defaultValue: 7,
                max: 50,
                min: 1,
                fieldType: "General",
            },
        ],
    },
};

export default productCategories;
